import { Client, Product, ProductUpdateDto } from "./api-client"

const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:5067"

export interface ProductInquiryRequest {
  productId: number
  phone: string
  message: string
}

export interface WarehouseInventoryAvailabilityDto {
  productId: number
  warehouseId: number
  availableQuantity: number
  hasLiveInventory: boolean
}

class AuthorizedClient extends Client {
  protected transformOptions(options: RequestInit): Promise<RequestInit> {
    const token = localStorage.getItem("meatbid_token")
    if (token) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      }
    }

    // Fix for multipart/form-data boundary issue
    const headers = options.headers as Record<string, string>
    if (
      headers &&
      (headers["Content-Type"] === "multipart/form-data" ||
        headers["content-type"] === "multipart/form-data")
    ) {
      delete headers["Content-Type"]
      delete headers["content-type"]
    }

    return Promise.resolve(options)
  }

  public override async createProduct(
    name?: string | undefined,
    sku?: string | undefined,
    description?: string | null | undefined,
    price?: number | undefined,
    unitCost?: number | undefined,
    weight?: number | undefined,
    weightUnit?: string | undefined,
    initialStock?: number | undefined,
    lowStockThreshold?: number | undefined,
    leadTimeDays?: number | undefined,
    safetyStockLevel?: number | undefined,
    isTaxable?: boolean | undefined,
    categoryId?: number | null | undefined,
    providerId?: number | null | undefined,
    warehouseId?: number | null | undefined,
    primaryImageIndex?: number | undefined,
    consumptionType?: string | undefined,
    images?: File | File[] | null | undefined,
    technicalSpecification?: File | null | undefined
  ): Promise<Product> {
    const url = `${baseUrl}/api/products`
    const token = localStorage.getItem("meatbid_token")

    const formData = new FormData()
    if (name) formData.append("Name", name)
    if (sku) formData.append("SKU", sku)
    if (description) formData.append("Description", description)
    if (price !== undefined) formData.append("Price", price.toString())
    if (unitCost !== undefined) formData.append("UnitCost", unitCost.toString())
    if (weight !== undefined) formData.append("Weight", weight.toString())
    if (weightUnit) formData.append("WeightUnit", weightUnit)
    if (initialStock !== undefined) formData.append("InitialStock", initialStock.toString())
    if (lowStockThreshold !== undefined)
      formData.append("LowStockThreshold", lowStockThreshold.toString())
    if (leadTimeDays !== undefined) formData.append("LeadTimeDays", leadTimeDays.toString())
    if (safetyStockLevel !== undefined)
      formData.append("SafetyStockLevel", safetyStockLevel.toString())
    if (isTaxable !== undefined) formData.append("IsTaxable", isTaxable.toString())
    if (categoryId !== undefined && categoryId !== null)
      formData.append("CategoryId", categoryId.toString())
    if (providerId !== undefined && providerId !== null)
      formData.append("ProviderId", providerId.toString())
    if (warehouseId !== undefined && warehouseId !== null)
      formData.append("WarehouseId", warehouseId.toString())
    if (primaryImageIndex !== undefined)
      formData.append("PrimaryImageIndex", primaryImageIndex.toString())
    if (consumptionType !== undefined) formData.append("ConsumptionType", consumptionType)

    if (images && Array.isArray(images)) {
      images.forEach((file) => formData.append("Images", file))
    } else if (images) {
      formData.append("Images", images)
    }

    if (technicalSpecification) {
      formData.append("TechnicalSpecification", technicalSpecification)
    }

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: formData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error creating product: ${response.status} - ${errorText}`)
    }

    return await response.json()
  }

  public override async updateProduct(id: number, body: ProductUpdateDto): Promise<Product> {
    const url = `${baseUrl}/api/products/${id}`
    const token = localStorage.getItem("meatbid_token")

    const formData = new FormData()
    if (body.name) formData.append("Name", body.name)
    if (body.sku) formData.append("SKU", body.sku)
    if (body.description) formData.append("Description", body.description)
    if (body.price !== undefined) formData.append("Price", body.price.toString())
    if (body.unitCost !== undefined) formData.append("UnitCost", body.unitCost.toString())
    if (body.weight !== undefined) formData.append("Weight", body.weight.toString())
    if (body.weightUnit) formData.append("WeightUnit", body.weightUnit)
    if (body.stockQuantity !== undefined)
      formData.append("StockQuantity", body.stockQuantity.toString())
    if (body.lowStockThreshold !== undefined)
      formData.append("LowStockThreshold", body.lowStockThreshold.toString())
    if (body.leadTimeDays !== undefined)
      formData.append("LeadTimeDays", body.leadTimeDays.toString())
    if (body.safetyStockLevel !== undefined)
      formData.append("SafetyStockLevel", body.safetyStockLevel.toString())
    if (body.isTaxable !== undefined) formData.append("IsTaxable", body.isTaxable.toString())
    if (body.consumptionType !== undefined) formData.append("ConsumptionType", body.consumptionType)
    if (body.categoryId !== undefined && body.categoryId !== null)
      formData.append("CategoryId", body.categoryId.toString())
    if (body.providerId !== undefined && body.providerId !== null)
      formData.append("ProviderId", body.providerId.toString())
    if (body.primaryImageIndex !== undefined)
      formData.append("PrimaryImageIndex", body.primaryImageIndex.toString())

    if (body.deletedImageIds && Array.isArray(body.deletedImageIds)) {
      body.deletedImageIds.forEach((id) => formData.append("DeletedImageIds", id.toString()))
    }

    if (body.images && Array.isArray(body.images)) {
      body.images.forEach((file) => formData.append("Images", file))
    } else if (body.images) {
      formData.append("Images", body.images)
    }

    if (body.technicalSpecification) {
      formData.append("TechnicalSpecification", body.technicalSpecification)
    }

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: formData,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Error updating product: ${response.status} - ${errorText}`)
    }

    return await response.json()
  }

  public async sendProductInquiry(body: ProductInquiryRequest): Promise<void> {
    const token = localStorage.getItem("meatbid_token")
    const response = await fetch(`${baseUrl}/api/contact/product-inquiry`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const errorText = await response.text()
      const error = new Error(`Error sending product inquiry: ${response.status} - ${errorText}`)
      ;(error as Error & { status?: number }).status = response.status
      throw error
    }
  }

  public async getWarehouseInventoryAvailability(
    productIds: number[]
  ): Promise<WarehouseInventoryAvailabilityDto[]> {
    const uniqueProductIds = Array.from(new Set(productIds.filter(Number.isFinite)))
    if (uniqueProductIds.length === 0) return []

    const params = new URLSearchParams()
    uniqueProductIds.forEach((productId) => params.append("productIds", productId.toString()))

    const token = localStorage.getItem("meatbid_token")
    const response = await fetch(`${baseUrl}/api/warehouses/inventory-availability?${params}`, {
      headers: {
        Accept: "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Error loading warehouse inventory availability: ${response.status} - ${errorText}`
      )
    }

    return await response.json()
  }

  /**
   * Helper to ensure a URL is absolute by prepending baseUrl if it's a relative path.
   */
  public ensureAbsUrl(url: string | undefined | null): string | undefined {
    if (!url) return undefined
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("blob:") ||
      url.startsWith("data:")
    ) {
      return url
    }
    // Remove leading slash if it exists and baseUrl ends with one, or vice-versa
    const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    const cleanUrl = url.startsWith("/") ? url : `/${url}`
    return `${cleanBase}${cleanUrl}`
  }
}

export const apiClient = new AuthorizedClient(baseUrl)

